import { apiClient } from "@/services/apiClient";

/** Mirrors the backend's ModerationChannel union. */
export type ModerationChannel =
  | "clarification_ask"
  | "clarification_answer"
  | "quote_message"
  | "quote_line_item"
  | "rfq_text"
  | "correction_request";

export type ModerationAuthorRole = "consumer" | "professional";

/** What happened to the message, as distinct from why. */
export type ModerationAction = "blocked" | "flagged" | "unreviewed";

export interface ModerationFlagSummary {
  userId: string;
  displayName: string | null;
  email: string | null;
  role: ModerationAuthorRole;
  isNotary: boolean;
  isAccountant: boolean;
  /** Blocked + flagged. Excludes 'unreviewed', which records an outage. */
  flagCount: number;
  /** Of those, how many were stopped outright. */
  blockedCount: number;
  channels: ModerationChannel[];
  lastBlockedAt: string;
}

export interface ModerationFlagDetail {
  id: string;
  /** Which role the author was acting in — the list groups by it, this endpoint does not. */
  role: ModerationAuthorRole;
  channel: ModerationChannel;
  reason: string;
  action: ModerationAction;
  /** The model's confidence, or null when only the deterministic layer ran. */
  aiScore: number | null;
  /** The offending text, verbatim — the point of the screen, not a detail. */
  content: string;
  details: Array<{ kind: string; text: string }>;
  quoteRequestId: string | null;
  legId: string | null;
  createdAt: string;
}

/**
 * Who has had a message blocked for carrying contact details, worst first.
 *
 * One row per (person, role): someone who is both a consumer and a
 * professional appears twice on purpose, because those are two different
 * relationships with the platform.
 */
export function listModerationFlags(params: {
  limit: number;
  offset: number;
}): Promise<{ items: ModerationFlagSummary[]; total: number }> {
  return apiClient
    .get<{ items: ModerationFlagSummary[]; total: number }>("/platform/moderation/flags", {
      params,
    })
    .then((res) => res.data);
}

/** One person's blocked messages, newest first. */
export function getModerationFlagsForUser(
  userId: string,
): Promise<{ items: ModerationFlagDetail[] }> {
  return apiClient
    .get<{ items: ModerationFlagDetail[] }>(`/platform/moderation/flags/${userId}`)
    .then((res) => res.data);
}
